import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Cookies from "js-cookie";
import { getPartnerAuthHeaders } from "@/lib/apiAuth";
import { toast } from "sonner";
import { AlertTriangle, Loader2, X } from "lucide-react";
import { getTierById } from "@/constants/aiTiersCatalog";
import {
  formatMonthlyUsd,
  getTierRetailMidpoint,
  previewMargin,
  retailSourceLabel,
} from "@/lib/partnerMargin";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const extractErrorMessage = (err, fallback) => {
  if (err?.response?.data?.detail) {
    const detail = err.response.data.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  }
  if (err?.response?.data?.message) return err.response.data.message;
  if (err?.request && !err?.response) {
    return "Network error. Please check your connection and try again.";
  }
  return fallback;
};

const SetRetailModal = ({ client, open, onClose, onSuccess }) => {
  const navigate = useNavigate();
  const [retailInput, setRetailInput] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    if (open && client) {
      const initial =
        client.partnerRetailMonthlyUsd != null
          ? String(client.partnerRetailMonthlyUsd)
          : "";
      setRetailInput(initial);
      setSubmitError("");
    }
  }, [open, client]);

  if (!open || !client) return null;

  const tierInfo = getTierById(client.tier);
  const midpoint = getTierRetailMidpoint(client.tier);
  const parsedRetail = retailInput.trim() === "" ? null : Number(retailInput);
  const preview =
    parsedRetail != null && !Number.isNaN(parsedRetail)
      ? previewMargin(client.tier, parsedRetail)
      : null;
  const belowWholesale =
    preview != null && tierInfo && parsedRetail < tierInfo.wholesale;

  const getAuthHeader = () => getPartnerAuthHeaders();

  const patchRetail = async (retailMonthlyUsd) => {
    setIsSubmitting(true);
    setSubmitError("");
    try {
      const res = await axios.patch(
        `${BACKEND_URL}/api/partner/clients/${client.id}/retail`,
        { retail_monthly_usd: retailMonthlyUsd },
        { headers: getAuthHeader() }
      );
      const label =
        retailMonthlyUsd == null
          ? "Using tier estimate for retail price"
          : "Retail price saved";
      toast.success(label);
      onSuccess?.(res.data);
      onClose();
    } catch (err) {
      if (err?.response?.status === 401) {
        Cookies.remove("token");
        Cookies.remove("email");
        navigate("/login");
        return;
      }
      const message = extractErrorMessage(err, "Could not save retail price. Please try again.");
      setSubmitError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    if (retailInput.trim() === "") {
      setSubmitError("Enter a retail price or use tier estimate.");
      return;
    }
    if (parsedRetail == null || Number.isNaN(parsedRetail) || parsedRetail < 1) {
      setSubmitError("Enter a valid whole-dollar amount ($1 or more).");
      return;
    }
    await patchRetail(Math.round(parsedRetail));
  };

  const handleUseEstimate = async () => {
    if (isSubmitting) return;
    await patchRetail(null);
  };

  const hasCustom = client.partnerRetailMonthlyUsd != null;
  const isUnchanged =
    hasCustom && parsedRetail === client.partnerRetailMonthlyUsd;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
      data-testid="set-retail-modal"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-2xl border border-partner bg-[#11151F] shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-5 sm:p-6 border-b border-partner">
          <div>
            <h2 className="text-lg font-bold text-partner">Set retail price</h2>
            <p className="text-sm text-partner-muted font-body mt-1">
              {client.name} · Tier {client.tier}
              {tierInfo ? ` ($${tierInfo.wholesale}/mo wholesale)` : ""}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-partner-muted hover:text-partner hover:bg-white/5"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSave} className="p-5 sm:p-6 space-y-5">
          <p className="text-xs text-partner-muted font-body">
            For your analytics only (not billed through the platform). If unset, we use the tier
            retail midpoint ({formatMonthlyUsd(midpoint)}).
          </p>

          <div>
            <label
              htmlFor="partner-retail-input"
              className="block text-xs font-mono uppercase tracking-wider text-partner-muted mb-2"
            >
              Your retail price ($/mo)
            </label>
            <input
              id="partner-retail-input"
              type="number"
              min="1"
              step="1"
              value={retailInput}
              onChange={(e) => {
                setRetailInput(e.target.value);
                setSubmitError("");
              }}
              placeholder={midpoint != null ? String(midpoint) : ""}
              className="w-full partner-input rounded-lg px-4 py-2.5 text-sm font-body"
              data-testid="set-retail-input"
            />
          </div>

          {tierInfo && (
            <div className="rounded-xl border border-partner bg-[#080A0F]/50 px-4 py-3 text-sm font-body space-y-1">
              <p className="text-partner-muted">
                Tier retail range: {tierInfo.retailLabel}
              </p>
              <p className="text-partner-muted">
                Suggested midpoint: {formatMonthlyUsd(midpoint)}
              </p>
              {preview != null && (
                <p className={preview >= 0 ? "text-emerald-400" : "text-amber-400"}>
                  Est. margin: {formatMonthlyUsd(preview)}
                </p>
              )}
            </div>
          )}

          {belowWholesale && (
            <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
              <p className="text-sm text-amber-100/90 font-body">
                Retail is below your wholesale cost (${tierInfo.wholesale}/mo). Margin will show as
                negative, but you can still save it for tracking.
              </p>
            </div>
          )}

          {client.retailSource && (
            <p className="text-xs text-partner-muted font-body">
              Current: {formatMonthlyUsd(client.retailMonthlyUsd)}{" "}
              <span className="text-partner-accent">({retailSourceLabel(client.retailSource)})</span>
            </p>
          )}

          {submitError && (
            <p className="text-sm text-red-400 font-body" data-testid="set-retail-error">
              {submitError}
            </p>
          )}

          <div className="flex flex-col gap-3 pt-1">
            <div className="flex flex-col-reverse sm:flex-row gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="flex-1 px-4 py-2.5 rounded-lg border border-partner text-partner-muted font-body text-sm hover:text-partner hover:bg-[#080A0F] disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting || isUnchanged}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#00D2FF] text-[#080A0F] font-semibold text-sm hover:bg-[#00D2FF]/90 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="set-retail-submit"
              >
                {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                {isSubmitting ? "Saving…" : "Save retail price"}
              </button>
            </div>
            {hasCustom && (
              <button
                type="button"
                onClick={handleUseEstimate}
                disabled={isSubmitting}
                className="w-full px-4 py-2.5 rounded-lg border border-partner text-partner-muted font-body text-sm hover:text-partner hover:border-[#00D2FF]/30 disabled:opacity-50"
                data-testid="set-retail-use-estimate"
              >
                Use tier estimate
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default SetRetailModal;
